import React, { useEffect, useState } from "react";
import _ from 'lodash'
import { useNavigation, useRoute } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import i18n from "utils/i18n";
import { PlaceholderCalendarStrip } from "placeholders";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout, { createRefreshControl } from "components/atoms/ThemedLayout";
import FetchError from "components/molecules/FetchError";
import GraphqlBuilder from "utils/request/graphql/builder";
import Text from "theme/Text";
import ThemedBlock from "components/atoms/ThemedBlock";
import Button from "theme/Button";
import { StyleSheet, View } from "react-native";
import { textStyles } from "styles/text";
import { bookingOptions, bookingStatus, bookingStatusActive } from "utils/constants/booking-constants";
import MutationEmployeeBookingStatus from "graphql/mutations/MutationEmployeeBookingStatus";
import { resetCart } from "services/redux/cart/actions";
import { cartConstants } from "services/redux/cart/constants";
import { SET_CART } from "services/redux/action-types";
import ObjectCart from "utils/objects/cart";
import ObjectBookingInvitation from "utils/objects/booking-invitation";
import InvitationBookings from "components/molecules/InvitationBookings";
import { constantsActive } from "utils/constants/constants";
import HelperString from 'utils/helper-string'
import FetchCachedMyBooking from "utils/fetch/cached/FetchCachedMyBooking";
import { getNavigateToFromSelect } from 'components/atoms/BookingCreate'
import TextDivider from 'components/atoms/TextDivider'
import ThemedBlockTitle from 'components/atoms/ThemedBlockTitle'
import ThemedButtonContainer from 'components/atoms/ThemedButtonContainer'
import FetchCachedEmployee from 'utils/fetch/cached/FetchCachedEmployee'
import HelperAlerts from 'utils/HelperAlerts'
import { routeBookingConstants } from 'utils/constants/route/RouteBookingConstants'
import GraphqlGroupedBuilder from 'utils/request/graphql/groupedBuilder'
import QueryBuildings from 'graphql/queries/QueryBuildings'
import HelperDate, { dateFormat } from 'utils/helper-date'

const HomeInvitationViewScene = () => {
	
	const route = useRoute();
	const dispatch = useDispatch();
	const navigation = useNavigation();
	
	const _cartTimeslotsSelected = useSelector( state => {
		return state.cart[cartConstants.TIMESLOTS_SELECTED];
	} );
	
	const [ saving, setSaving ] = useState( null )
	const [ loading, setLoading ] = useState( true )
	const [ error, setError ] = useState( false )
	const [ refreshing, setRefreshing ] = useState( false )
	const [ invitation, setInvitation ] = useState( null )
	const [ bookingsByDate, setBookingsByDate ] = useState( {} )
	const [ organizer, setOrganizer ] = useState( {} )
	const [ employees, setEmployees ] = useState( {} )
	const [ date, setDate ] = useState( 'null' )
	const [ didRespond, setDidRespond ] = useState( false )
	const [ isInFuture, setIsInFuture ] = useState( false )
	
	const employeeBookingInvitationGuid = _.get( route.params, 'employee_booking_invitation_guid', null )
	
	const fetch = () => {
		GraphqlGroupedBuilder.addQuery( 'buildings', QueryBuildings, {
			variables: {
				guid: employeeBookingInvitationGuid,
				employee_guid: '::employee_guid',
				employee_invited_date: HelperDate.date().format( dateFormat.DATE ) +'_after',
				with_inviter: constantsActive.YES,
				booking_active: [
					constantsActive.YES,
					constantsActive.NO,
				]
			},
			resultPath: [ 'employee_booking_invitation' ],
			callbackStart: () => {
				setLoading( true )
				setError( false )
			},
			callbackError: result => {
				setLoading( false )
				setRefreshing( false )
				setError( true )
			},
			callbackSuccess: result => {
				const employeeInvited = _.get( result, 'employee_invited', {} );
				const organizer = _.get( result, 'employee', {} );
				
				// Check response
				const inviteDidRespond = _.get( employeeInvited, 'responded', constantsActive.NO ) === constantsActive.YES;
				
				// Parse the invitation and only return 'my' bookings
				const { employees, bookingsByDate } = ObjectBookingInvitation.parse( result, {
					onlyMyBookings: false,
					inviteesHidden: _.get( result, 'show_invitees', 'yes' ) === 'no'
				} );
				
				const bookings = _.get( result, 'bookings', [] );
				
				let isInFuture = false;
				_.forEach( bookings, booking => {
					if( HelperDate.date( booking.booking_date, dateFormat.DATE ).isSameOrAfter( HelperDate.date() ) ) {
						isInFuture = true
					}
				});
				
				const date = ObjectBookingInvitation.getDatesString( bookings );
				
				setLoading( false )
				setRefreshing( false )
				setInvitation( result )
				setDidRespond( inviteDidRespond )
				setOrganizer( organizer )
				setEmployees( employees )
				setBookingsByDate( bookingsByDate )
				setDate( date )
				setIsInFuture( isInFuture )
			}
		})
	}
	
	const fetchEmployees = () => {
		FetchCachedEmployee.fetchEasy( {
			employee_guids: _.map( employees, o => {
				return o.guid;
			} )
		});
	}
	
	const onPressSave = () => {
		setSaving( bookingStatus.INVITE_ACCEPTED );
		
		// Check which ones were not selected
		const selectedTimeslotGuids = _.map( ObjectCart.getSelectedTimeslots(), o => {
			return o.guid;
		} );
		const rejectTimeslots = [];
		
		let booking_count = 0;
		_.forEach( bookingsByDate, ( bookings, date ) => {
			_.forEach( bookings.booking, booking => {
				booking_count++;
				
				if( !_.includes( selectedTimeslotGuids, booking.guid ) ) {
					if( !bookingStatusActive.includes( booking.status ) ) {
						rejectTimeslots.push( booking );
					}
				}
			})
		});
		
		// Add to the cart
		dispatch({
			type: SET_CART,
			key: cartConstants.BOOKING_REJECT,
			payload: rejectTimeslots
		})
		
		setSaving(null );
		
		const { navigateTo } = getNavigateToFromSelect( null, true );
		navigation.navigate( navigateTo, {
			[ routeBookingConstants.FOR_OPTION ]: bookingOptions.MYSELF
		} );
	}
	
	const onPressReject = () => {
		HelperAlerts.alert(
			i18n.t( 'page.invitation.view.alerts.cancel.title' ),
			i18n.t('page.invitation.view.alerts.cancel.message_all'),
			[
				{ text: i18n.t( 'basic.cancel' ), },
				{ text: i18n.t( 'basic.ok' ), onPress: onPressRejectConfirm.bind( this ) }
			]
		);
	}
	
	const onPressRejectConfirm = () => {
		const status = bookingStatus.INVITE_REJECTED;
		
		// Startup the builder
		const builder = new GraphqlBuilder();
		
		// Add the mutations to the query
		_.forEach( bookingsByDate, ( bookings, date ) => {
			_.forEach( bookings.booking, booking => {
				const active = [ ...bookingStatusActive ].includes( status );
				
				builder.addQuery( `mutation-booking-status-${booking.guid}`, MutationEmployeeBookingStatus, {
					guid: booking.guid,
					active: active ? constantsActive.YES : constantsActive.NO,
					status: status
				});
			})
		});
		
		builder.setOnStart( setSaving.bind( this, status ) );
		
		builder.setOnSuccess( result => {
			FetchCachedMyBooking.reload({
				callbackSuccess: () => {
					setSaving(null )
					navigation.goBack();
				}
			});
		});
		
		builder.setOnError( result => {
			setSaving( null )
		});
		
		builder.execute();
	}
	
	useEffect( () => {
		return fetchEmployees();
	}, [ employees ] )
	
	useEffect( () => {
		dispatch( resetCart() )
		
		fetch();
	}, [] );

	const selectedTimeslotGuids = _.map( ObjectCart.getSelectedTimeslots(), o => {
		return o.guid;
	} );

	let overwriteEmployeeStatus = {};
	_.forEach( selectedTimeslotGuids, ( guid ) => {
		overwriteEmployeeStatus[ guid ] = bookingStatus.INVITE_ACCEPTED;
	});

	// The one who initiated the invite
	const inviter = _.get( invitation, 'employee', {} );
	const invitationName = _.get( invitation, 'name', null );
	const invitationDescription = _.get( invitation, 'description', null );
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				title={ i18n.t(`page.booking.calendar.invitations.received`) }
				topNavigation={ true }
				showBackButton={ true }
				scrollViewProps={{
					...createRefreshControl({
						isRefreshing: refreshing,
						onRefresh: () => {
							setRefreshing( true )
							fetch()
						}
					})
				}}>
				
				<Choose>
					<When condition={ loading }>
						<PlaceholderCalendarStrip />
					</When>
					<When condition={ error }>
						<FetchError
							action={ () => {
								setLoading( true )
								fetch()
							} }
						/>
					</When>
					<When condition={ _.size( bookingsByDate ) === 0 }>
						<ThemedBlock
							transparent
							mediumPadding
							style={ styles.messageContainer }>
							
							<Text
								style={ textStyles.textCenter }>
								
								{ i18n.t('page.booking.invite.not_found' ) }
							</Text>
						</ThemedBlock>
					</When>
					<Otherwise>
						
						<View>
							{/* Create the block*/}
							<View>
								<TextDivider
									rowStyle={ styles.textDividerMarginTop12 }
									text={ i18n.t('page.booking.invite.title') }
								/>
								
								<ThemedBlock
									smallPadding
									shadow>
									
									<Text>
										{ i18n.t('page.booking.invite.description', {
											name: inviter?.name ?? '...',
											date: date
										}) }
									</Text>
								</ThemedBlock>
							</View>
							
							{/* Create the block*/}
							<If condition={ invitationName !== null || invitationDescription !== null }>
								<View>
									<TextDivider
										rowStyle={ styles.textDividerMarginTop12 }
										text={ i18n.t( 'constants.information' ) }
									/>
									
									<ThemedBlock
										smallPadding
										shadow>
										
										<If condition={ invitationName !== null }>
											<ThemedBlockTitle
												style={ invitationDescription === null ? styles.themedBlockTitleMarginBottom0 : {} }>
												
												{ invitationName }
											</ThemedBlockTitle>
										</If>
										
										<If condition={ invitationDescription !== null }>
											<Text>
												{ invitationDescription }
											</Text>
										</If>
									</ThemedBlock>
								</View>
							</If>
							
							{/*  */}
							<InvitationBookings
								key={ HelperString.hash('invitation-bookings', employeeBookingInvitationGuid ) }
								bookingsByDate={ bookingsByDate }
								organizer={ organizer }
								employees={ employees }
								selectable={ !didRespond && isInFuture }
								overwriteEmployeeStatus={ overwriteEmployeeStatus }
							/>
							
							{/*	The confirm or decline button */}
							{ (isInFuture && !didRespond) && (
								<ThemedButtonContainer>
									<Button
										isLoading={ saving === bookingStatus.INVITE_ACCEPTED }
										disabled={ !_cartTimeslotsSelected || (saving !== null && saving !== bookingStatus.INVITE_ACCEPTED) }
										style={ styles.buttonFlex50 }
										onPress={ onPressSave.bind( this ) }>
										
										{ i18n.t('basic.accept') }
									</Button>
									
									<Button
										status={"danger"}
										appearance={"ghost"}
										style={ styles.buttonFlex50 }
										isLoading={ saving === bookingStatus.INVITE_REJECTED }
										disabled={ saving !== null && saving !== bookingStatus.INVITE_REJECTED }
										onPress={ onPressReject.bind( this ) }>
										
										{ i18n.t('basic.reject') }
									</Button>
								</ThemedButtonContainer>
							)}
						</View>
					</Otherwise>
				</Choose>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	)
}

const styles = StyleSheet.create({
	textDividerMarginTop12: {
		marginTop: 12,
	},
	themedBlockTitleMarginBottom0: {
		marginBottom: 0,
	},
	buttonFlex50: {
		flex: 50,
	},
	messageContainer: {
		marginBottom: 0,
		paddingBottom: 0,
	},
	textBold: {
		fontWeight: 'bold',
	},
});


export default HomeInvitationViewScene
